import React, { useEffect, useState } from 'react';
import { Settings, Flame, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import StarCounter from './StarCounter';

interface StarTrackerHeaderProps {
  stars: number;
  editMode: boolean;
  themeEmoji: string;
  streakDays: number;
  starsCounterRef: React.RefObject<HTMLDivElement>;
  onUpdateTotalStars: (change: number) => void;
}

const StarTrackerHeader: React.FC<StarTrackerHeaderProps> = ({
  stars,
  editMode,
  themeEmoji,
  streakDays,
  starsCounterRef,
  onUpdateTotalStars
}) => {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Update time every minute for dynamic greeting
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);
  
  // Get theme name from emoji for styling
  const getThemeNameFromEmoji = (emoji: string): string => {
    switch (emoji) {
      case '🤖': return 'robots';
      case '🦄': return 'unicorn';
      case '🚀': return 'space';
      case '🌊': return 'ocean';
      case '🌳': return 'forest';
      case '🌅': return 'sunset';
      case '🌋': return 'volcano';
      case '🏎️': return 'trucks';
      case '🥷': return 'ninja';
      case '🧽': return 'spongebob';
      case '🦖': return 'dinosaurs';
      case '🍭': return 'candy';
      default: return 'robots';
    }
  };
  
  const themeName = getThemeNameFromEmoji(themeEmoji);
  
  
  
  
  return (
    <header className="relative w-full mb-3">
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Left: Balanced brand section */}
          <div className="flex items-center space-x-3">
            <div className="text-2xl">{themeEmoji}</div>
            <div className="flex items-center space-x-3">
              <h1 className={`
                text-3xl font-bold
                bg-clip-text text-transparent bg-gradient-to-r 
                ${themeName === 'robots' ? 'from-blue-600 to-cyan-500' :
                  themeName === 'unicorn' ? 'from-purple-600 to-pink-500' :
                  themeName === 'space' ? 'from-indigo-600 to-purple-500' :
                  themeName === 'forest' ? 'from-green-600 to-emerald-400' :
                  themeName === 'sunset' ? 'from-orange-500 to-pink-500' :
                  themeName === 'volcano' ? 'from-red-600 to-orange-500' :
                  themeName === 'trucks' ? 'from-lime-500 to-green-600' :
                  themeName === 'ninja' ? 'from-red-700 to-slate-500' :
                  themeName === 'spongebob' ? 'from-amber-700 to-cyan-700 dark:from-yellow-300 dark:to-cyan-300' :
                  themeName === 'dinosaurs' ? 'from-emerald-800 to-lime-600 dark:from-emerald-300 dark:to-lime-300' :
                  themeName === 'candy' ? 'from-pink-700 to-violet-700 dark:from-pink-300 dark:to-violet-300' :
                  'from-cyan-600 to-teal-400'}
              `}>
                Star Chores
              </h1>
              {streakDays > 0 && (
                <div className="flex items-center space-x-1">
                  <Flame size={12} className="text-orange-500" />
                  <span className="text-sm text-orange-600 dark:text-orange-400">
                    {streakDays}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {/* Right: Proportional star counter and settings */}
          <div className="flex items-center space-x-3">
            <div 
              ref={starsCounterRef}
              className="transform scale-90 origin-right"
            >
              <StarCounter 
                count={stars} 
                onUpdateStars={onUpdateTotalStars} 
                editMode={editMode}
              />
            </div>
            
            <button
              onClick={() => navigate('/settings')}
              className="p-2 rounded-lg bg-white/60 dark:bg-gray-800/60 hover:bg-white/80 dark:hover:bg-gray-800/80 transition-colors"
              aria-label="Settings"
            >
              <Settings size={20} className="text-gray-600 dark:text-gray-300" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default StarTrackerHeader;